import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonMenu,
  IonMenuToggle,
  IonTitle,
  IonToolbar,
  MenuController,
  ModalController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  businessOutline,
  calendarOutline,
  chatbubbleEllipsesOutline,
  closeOutline,
  colorPaletteOutline,
  documentTextOutline,
  flameOutline,
  helpCircleOutline,
  homeOutline,
  informationCircleOutline,
  logInOutline,
  logOutOutline,
  mapOutline,
  peopleCircleOutline,
  personOutline,
  phonePortraitOutline,
  searchOutline,
  settingsOutline,
  shareSocialOutline,
  shieldCheckmarkOutline,
  starOutline,
  statsChartOutline,
  timeOutline,
  warningOutline,
} from 'ionicons/icons';
import { ComponentAccess } from '../../../common/decorators/component-access.decorator';
import { HasAccessDirective } from '../../../common/directives/has-access.directive';
import { ProFeature } from '../../../common/enums/pro-feature-message.enum';
import { GUIDED_TOURS_MENU_LABEL, ONBOARDING_MENU_LABEL } from '../../features/sequal/onboarding/onboarding-slides.const';
import { OnboardingService, TourId } from '../../features/sequal/onboarding/onboarding.service';
import { TourService } from '../../features/sequal/onboarding/tour.service';
import { GuidedTourEntry, TOUR_MENU } from '../../features/sequal/onboarding/tours';
import { GuidedToursModalComponent } from '../../features/sequal/onboarding/guided-tours-modal.component';
import { launchGuidedTour } from '../../features/sequal/onboarding/guided-tour-launcher';
import { SyncRowComponent } from '../../features/sequal/components/sync-row/sync-row.component';
import { SupportAvailabilityService } from '../../features/support/services/support-availability.service';
import { PublishGateService } from '../../features/sequal/services/publish-gate.service';
import { ProGateService } from '../../core/payment/services/pro-gate.service';
import { SubscriptionService } from '../../core/payment/services/subscription.service';
import { AppRefreshCoordinator } from '../../core/visibility/app-refresh-coordinator.service';
import { RefreshKey } from '../../core/visibility/refresh-key.enum';
import { AuthenticationService } from '../../../common/services/authentication/authentication.service';
import { CommonRoute } from '../../../common/enums/common-route.enum';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  standalone: true,
  imports: [
    IonMenu,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonButtons,
    IonContent,
    IonItem,
    IonMenuToggle,
    IonLabel,
    IonIcon,
    RouterLink,
    RouterLinkActive,
    SyncRowComponent,
    HasAccessDirective,
  ],
})
@ComponentAccess('MenuComponent')
export class MenuComponent {
  protected readonly CommonRoute = CommonRoute;
  private menuCtrl = inject(MenuController);
  private proGate = inject(ProGateService);
  private subscription = inject(SubscriptionService);
  private auth = inject(AuthenticationService);
  private onboarding = inject(OnboardingService);
  private tour = inject(TourService);
  private modalCtrl = inject(ModalController);
  private publishGate = inject(PublishGateService);
  private supportAvailability = inject(SupportAvailabilityService);
  private refreshCoordinator = inject(AppRefreshCoordinator);

  /** Hides the My listings row where publishing is not live. The server owns
   *  that switch, so the menu asks the gate rather than a build-time flag. */
  protected readonly publishAvailable = this.publishGate.available;
  /** Hides Help & Support wholesale when the api reports it switched off. */
  protected readonly supportAvailable = this.supportAvailability.enabled;

  protected readonly OnboardingMenuLabel = ONBOARDING_MENU_LABEL;
  protected readonly GuidedToursMenuLabel = GUIDED_TOURS_MENU_LABEL;

  /**
   * Router-link matching for the menu rows. `subset` (not `exact`) so a row
   * stays lit on that page's detail/sub routes. Segment-wise, so sibling
   * /account/* links stay exclusive.
   */
  protected readonly MENU_LINK_MATCH = {
    paths: 'subset',
    queryParams: 'ignored',
    matrixParams: 'ignored',
    fragment: 'ignored',
  } as const;

  /** Drives which menu items show (account links + sign in/out). */
  readonly isAuthenticated = toSignal(this.auth.isAuthenticated(), { initialValue: false });
  /** Same "Go Premium" gate as Settings' CTA. */
  readonly isPremium = toSignal(this.subscription.isPro$, { initialValue: this.subscription.isPro });
  /** Trial-only Pro (ADR-0120) — Premium, but still shown Go Premium to upgrade. */
  readonly isOnTrial = toSignal(this.subscription.onTrial$, { initialValue: this.subscription.onTrial });
  /** Show "Go Premium" to anyone who isn't already Pro — guests included — or who
   *  is only on the free trial (ADR-0120), so they can upgrade to paid. A guest
   *  taps it → paywall's guest state → sign up/in → the intent bridge resumes the
   *  Pro flow afterwards (ProGateService / PaywallHostComponent). */
  /** ...and never when purchasing is switched off for this channel (ADR-0173):
   *  the row would open nothing, and a "Go Premium" label is itself an offer to
   *  sell. */
  readonly canShowGoPremium = computed(
    () =>
      this.proGate.purchasingEnabled() &&
      (!this.isPremium() || this.isOnTrial()),
  );

  constructor() {
    addIcons({
      homeOutline,
      searchOutline,
      calendarOutline,
      settingsOutline,
      closeOutline,
      shareSocialOutline,
      statsChartOutline,
      flameOutline,
      helpCircleOutline,
      informationCircleOutline,
      mapOutline,
      personOutline,
      // Org user management. peopleOutline is already My listings, and this row
      // is about who the people ARE rather than what they published.
      peopleCircleOutline,
      // Organisation activity — a record of when things happened.
      timeOutline,
      colorPaletteOutline,
      businessOutline,
      shieldCheckmarkOutline,
      phonePortraitOutline,
      documentTextOutline,
      warningOutline,
      logInOutline,
      logOutOutline,
      starOutline,
      // Help & Support. helpCircleOutline is already the "Getting started" row
      // and helpBuoyOutline is About → Contact Support, so this one gets the
      // conversation icon — which is also what the screen actually is.
      chatbubbleEllipsesOutline,
    });
  }

  closeMenu() {
    this.menuCtrl.close();
  }

  /** Opens the Pro paywall (same call as Settings' "Go Premium" row). */
  goPremium(): void {
    this.proGate.open(ProFeature.GoPremium);
  }

  /** Replays the first-run welcome carousel (and, from its CTA, the tour).
   *  `OnboardingHostComponent` owns the modal. */
  replayOnboarding(): void {
    this.onboarding.replayWelcome();
  }

  /**
   * Opens the guided-tour chooser: a modal (the app's picker convention — not a
   * bottom action sheet) of the navigable tours, each of which routes to its
   * page (or opens the menu) and replays. The authoring tours aren't here — they
   * replay from a button in each editor's header, where their targets live.
   *
   * Launch happens after the modal has dismissed, so a routed tour's spotlight
   * isn't drawn under a closing modal.
   */
  async openGuidedTours(): Promise<void> {
    await this.menuCtrl.close();
    const modal = await this.modalCtrl.create({ component: GuidedToursModalComponent });
    await modal.present();
    const { data, role } = await modal.onWillDismiss<GuidedTourEntry>();
    if (role === 'selected' && data) await launchGuidedTour(this.tour, this.menuCtrl, data);
  }

  /** First time the menu is opened: point out what's in here. Waits for
   *  `ionDidOpen` — the items are off-canvas until the panel has settled, so an
   *  earlier hook would measure them at the wrong place. */
  onMenuOpened(): void {
    this.tour.maybeStart(TourId.Menu, TOUR_MENU);
    // Help & Support and My listings are drawn here only while their switch is
    // on, so this is where a stale or failed-closed answer is pulled again —
    // their route guards can never run for an entry the user cannot see.
    void this.refreshCoordinator.refreshIfStale(RefreshKey.SupportAvailability);
    void this.refreshCoordinator.refreshIfStale(RefreshKey.PublishingAvailability);
  }

}
